import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendingUp, DollarSign, Users, Share2, Copy, Clock,
  XCircle, Wallet, ArrowUpRight, ArrowDownRight, Award, Sparkles,
  CheckCircle2, Mail, KeyRound, ShieldCheck, ExternalLink, LogIn,
} from "lucide-react";
import { api } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { ProfileShell } from "../../components/profile/ProfileShell";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";

export default function AffiliatePage() {
  const [app, setApp] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null); // {user_id, username}
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/affiliate/application");
        setApp(data);
        if (data.is_affiliate) {
          const { data: d } = await api.get("/profile/affiliate/dashboard");
          setDash(d);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load affiliate");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (s) => {
    const ok = await copyToClipboard(s);
    if (ok) toast.success("Copied!");
    else toast.error("Could not copy");
  };

  const openPlayer = async (p) => {
    setSelectedPlayer({ user_id: p.user_id, username: p.username });
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/profile/affiliate/player/${p.user_id}/history`);
      setHistoryData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load player history");
      setSelectedPlayer(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (loading) {
    return <ProfileShell title="Affiliate Program"><div className="py-10 text-center text-white/60">Loading...</div></ProfileShell>;
  }

  // Approved affiliate — show Promo Panel banner ONLY.
  // All other affiliate features (referrals, KPIs, commissions, promo code mgmt, payouts)
  // are now exclusively managed from the dedicated /promo panel.
  if (app?.is_affiliate && dash) {
    return (
      <ProfileShell title="Affiliate Dashboard">
        <div className="pb-10 space-y-4" data-testid="affiliate-dashboard">
          {/* ========== Promo & Affiliate Panel access banner ========== */}
          <PromoPanelBanner dash={dash} />
        </div>
      </ProfileShell>
    );
  }

  // Application pending / rejected / none
  const status = app?.application?.status;
  return (
    <ProfileShell title="Affiliate Program">
      <div className="pb-10" data-testid="affiliate-apply-page">
        {/* Hero */}
        <div
          className="rounded-2xl p-6 relative overflow-hidden"
          style={{
            background:
              "radial-gradient(800px 400px at 20% 0%, rgba(253,224,71,0.2) 0%, rgba(253,224,71,0) 55%), linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.1))",
            border: "1px solid rgba(253,224,71,0.35)",
          }}
        >
          <div className="flex items-center gap-2 text-amber-200 text-[11px] font-bold tracking-[0.22em] uppercase">
            <Sparkles size={14} /> Partner With Us
          </div>
          <div className="mt-2 text-3xl sm:text-4xl font-black text-white leading-tight">
            Earn <span className="text-amber-300">{app?.config?.commission_pct || 30}%</span> of your
            <br />players&apos; net loss
          </div>
          <div className="mt-2 text-sm text-white/80 max-w-md">
            Become an affiliate and earn commission every {app?.config?.period_days || 15} days from the net loss of players you bring in. Paid directly to your balance.
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 max-w-lg">
            <Stat icon={Users} label="Invite friends" />
            <Stat icon={TrendingUp} label={`${app?.config?.period_days || 15}-day cycle`} />
            <Stat icon={DollarSign} label={`${app?.config?.commission_pct || 30}% share`} />
          </div>
        </div>

        {status === "pending" && (
          <div className="mt-5 rounded-xl p-5 text-center" style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.5)" }}>
            <Clock size={28} className="text-amber-400 mx-auto mb-2" />
            <div className="text-white font-bold text-lg">Application Under Review</div>
            <div className="text-white/70 text-sm mt-1">Our team is reviewing your application. You&apos;ll be notified within 48 hours.</div>
            <div className="text-[11px] text-white/50 mt-2">Submitted: {new Date(app.application.created_at).toLocaleString()}</div>
          </div>
        )}

        {status === "rejected" && (
          <div className="mt-5 rounded-xl p-5 text-center" style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)" }}>
            <XCircle size={28} className="text-rose-400 mx-auto mb-2" />
            <div className="text-white font-bold text-lg">Application Not Approved</div>
            {app.application.notes && <div className="text-white/70 text-sm mt-1 italic">“{app.application.notes}”</div>}
            <div className="text-[11px] text-white/50 mt-2">You can reapply by submitting a new application below.</div>
            <button
              onClick={() => navigate("/profile/affiliate-apply")}
              className="mt-3 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold"
            >
              Reapply
            </button>
          </div>
        )}

        {(!status || status === "rejected") && (
          <div className="mt-5 text-center">
            <button
              onClick={() => navigate("/profile/affiliate-apply")}
              className="px-6 py-3 rounded-xl text-base font-black text-white uppercase tracking-wider"
              style={{
                background: "linear-gradient(180deg, #22c073 0%, #159654 50%, #0d6b3a 100%)",
                boxShadow: "0 10px 30px rgba(13,107,58,0.5)",
              }}
              data-testid="aff-apply-now-btn"
            >
              Apply Now
            </button>
          </div>
        )}

        {/* How it works */}
        <div className="mt-6">
          <div className="text-sm font-semibold text-white mb-3">How it works</div>
          <div className="space-y-2">
            {[
              { n: 1, t: "Apply", d: "Submit your application with traffic source and promo plan." },
              { n: 2, t: "Get approved", d: "We review within 48 hours. Approved affiliates get a unique affiliate link and promo code." },
              { n: 3, t: "Share & earn", d: `Users who sign up via your link or promo code become your players. Earn ${app?.config?.commission_pct || 30}% of their realised net loss.` },
              { n: 4, t: "Get paid", d: `Commissions settle every ${app?.config?.period_days || 15} days, credited straight to your balance.` },
            ].map((s) => (
              <div key={s.n} className="flex gap-3 rounded-lg p-3 bg-black/25 border border-white/5">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold text-sm shrink-0">{s.n}</div>
                <div>
                  <div className="text-white font-semibold text-sm">{s.t}</div>
                  <div className="text-white/65 text-[12px] mt-0.5">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ProfileShell>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl p-3 bg-black/30 border border-white/10">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50">
        <Icon size={12} style={{ color }} /> {label}
      </div>
      <div className="text-xl font-black text-white tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function Stat({ icon: Icon, label }) {
  return (
    <div className="rounded-lg px-2.5 py-2 text-center" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <Icon size={16} className="mx-auto text-amber-300 mb-1" />
      <div className="text-[11px] text-white/80 font-semibold">{label}</div>
    </div>
  );
}


function PlayerHistoryModal({ open, onClose, loading, data, fallbackUsername }) {
  const summary = data?.summary;
  const player = data?.player;
  const txs = data?.transactions || [];
  const cutoff = data?.cutoff;
  const title = player?.username || fallbackUsername || "Player";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-2xl border-0 p-0 gap-0 bg-transparent shadow-none backdrop-blur-md"
        data-testid="aff-player-history-modal"
      >
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(160deg, #0d1f15 0%, #0a0d12 100%)",
            border: "1px solid rgba(16,185,129,0.35)",
            boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
          }}
        >
          {/* Header */}
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">Player History</div>
                <DialogTitle className="text-white text-xl font-black truncate" data-testid="aff-history-title">
                  {title}
                </DialogTitle>
                {player?.phone && (
                  <div className="text-[12px] text-white/50 font-mono mt-0.5">{player.phone}</div>
                )}
              </div>
            </div>
          </DialogHeader>

          {loading && (
            <div className="py-12 text-center text-white/50 text-sm" data-testid="aff-history-loading">
              Loading transactions…
            </div>
          )}

          {!loading && summary && (
            <>
              {/* Summary grid */}
              <div className="px-5 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                <SumCell label="Deposits" value={`৳${summary.total_deposits.toFixed(0)}`} color="#34d399" testid="aff-history-sum-deposits" />
                <SumCell label="Withdrawals" value={`৳${summary.total_withdrawals.toFixed(0)}`} color="#fbbf24" testid="aff-history-sum-withdrawals" />
                <SumCell label="Net Loss" value={`৳${summary.net_loss.toFixed(0)}`} color="#f87171" testid="aff-history-sum-netloss" />
                <SumCell label={`Commission (${summary.commission_pct}%)`} value={`৳${summary.commission.toFixed(2)}`} color="#34d399" emphasize testid="aff-history-sum-commission" />
              </div>

              {cutoff?.cleared_by_payout && (
                <div className="mx-5 mt-3 rounded-lg px-3 py-2 text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30" data-testid="aff-history-cutoff-notice">
                  Showing transactions since last payout · {new Date(cutoff.since).toLocaleString()}
                </div>
              )}

              {/* Transactions list */}
              <div className="px-5 pt-4 pb-5">
                <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2 flex items-center justify-between">
                  <span>Transactions ({summary.transaction_count})</span>
                  {summary.transaction_count > 6 && <span className="text-white/30 normal-case tracking-normal">scroll for more</span>}
                </div>
                <div className="rounded-xl border border-white/10 bg-black/30 max-h-[420px] overflow-y-auto" data-testid="aff-history-tx-list">
                  {txs.length === 0 && (
                    <div className="py-10 text-center text-white/40 text-sm" data-testid="aff-history-empty">
                      {cutoff?.cleared_by_payout
                        ? "No transactions since the last payout."
                        : "No transactions yet."}
                    </div>
                  )}
                  {txs.map((t, i) => {
                    const isDep = t.kind === "deposit";
                    return (
                      <div
                        key={`${t.id}-${i}`}
                        className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-white/5 last:border-0"
                        data-testid={`aff-history-tx-${i}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                            style={{ background: isDep ? "rgba(16,185,129,0.18)" : "rgba(245,158,11,0.18)" }}
                          >
                            {isDep ? (
                              <ArrowDownRight size={15} className="text-emerald-300" />
                            ) : (
                              <ArrowUpRight size={15} className="text-amber-300" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white text-sm font-semibold capitalize">
                              {t.kind} <span className="text-white/40 text-[10px] font-normal uppercase tracking-wider ml-1">via {t.source}</span>
                            </div>
                            <div className="text-white/50 text-[11px]">
                              {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                              {t.method ? ` · ${t.method}` : ""}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-base font-black tabular-nums ${isDep ? "text-emerald-300" : "text-amber-300"}`}>
                            {isDep ? "+" : "−"}৳{t.amount.toFixed(0)}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-white/40">{t.status}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SumCell({ label, value, color, emphasize, testid }) {
  return (
    <div
      className="rounded-lg p-2.5"
      style={{
        background: emphasize ? "rgba(16,185,129,0.12)" : "rgba(0,0,0,0.35)",
        border: emphasize ? "1px solid rgba(16,185,129,0.4)" : "1px solid rgba(255,255,255,0.08)",
      }}
      data-testid={testid}
    >
      <div className="text-[9.5px] uppercase tracking-widest text-white/45 font-semibold">{label}</div>
      <div className="text-base font-black tabular-nums mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}


/* ============================================================
 * PromoPanelBanner — shown to approved affiliates explaining how
 * to access the dedicated /promo panel (1xbet-style) where they can
 * manage promo codes, 2FA, payouts, etc.
 * ============================================================ */
function PromoPanelBanner({ dash }) {
  const pa = dash?.panel_account;
  const panelUrl = dash?.panel_url || "/promo/login";
  const periodDays = dash?.config?.period_days || 15;

  return (
    <div
      className="rounded-2xl overflow-hidden relative"
      style={{
        background:
          "radial-gradient(900px 400px at 90% 0%, rgba(251,191,36,0.25), transparent 60%), linear-gradient(135deg, rgba(34,192,115,0.18), rgba(13,107,58,0.05))",
        border: "1px solid rgba(34,192,115,0.55)",
        boxShadow: "0 12px 40px rgba(13,107,58,0.25)",
      }}
      data-testid="promo-panel-banner"
    >
      <div className="absolute top-0 right-0 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.2em] rounded-bl-xl"
        style={{ background: "linear-gradient(135deg,#22c073,#fbbf24)", color: "#0a1018" }}>
        NEW · MAXLIVE88.COM/PROMO
      </div>

      <div className="p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <CheckCircle2 size={16} className="text-emerald-300" />
          <span className="text-emerald-200 text-[11px] font-bold tracking-[0.22em] uppercase">
            Application Approved
          </span>
        </div>

        <div className="text-xl sm:text-2xl font-black text-white leading-tight mb-2">
          Your <span className="text-emerald-300">Promo &amp; Affiliate Panel</span> is ready
        </div>
        <div className="text-[13px] text-white/75 leading-relaxed mb-4 max-w-2xl">
          A dedicated panel for full control: manage your promo code, see referrals, enable 2FA,
          and submit payout requests via Binance Pay / USDT / Bkash / Nagad. Commissions are
          paid every <b className="text-amber-300">{periodDays} days</b>.
        </div>

        {/* Credentials info row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          <CredBlock
            icon={Mail}
            color="#3b82f6"
            label="Credentials sent to"
            value={pa?.email_masked || "your email"}
            hint="Check inbox / spam"
          />
          <CredBlock
            icon={KeyRound}
            color="#fbbf24"
            label="Your panel username"
            value={pa?.username || "—"}
            hint={pa?.must_change_password ? "Change password on first login" : "Password change anytime"}
          />
          <CredBlock
            icon={ShieldCheck}
            color="#22c55e"
            label="Two-Factor Auth"
            value={pa?.two_factor_enabled ? "Enabled ✓" : "Recommended"}
            hint="Add 2FA inside the panel"
          />
        </div>

        {/* Step list */}
        <div className="bg-black/30 border border-white/10 rounded-xl p-3 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-white/55 font-semibold mb-2">How to use</div>
          <ol className="space-y-1.5">
            {[
              { n: 1, t: "Open ", b: "maxlive88.com/promo", a: ", click Sign In" },
              { n: 2, t: "Use the username + password sent to your registered email" },
              { n: 3, t: `Change password on first login → optionally enable 2FA → request payouts every ${periodDays} days` },
            ].map((s) => (
              <li key={s.n} className="flex items-start gap-2 text-[12px] text-white/80">
                <span className="shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold text-[10px]">{s.n}</span>
                <span>
                  {s.t}
                  {s.b && <code className="px-1.5 py-0.5 mx-1 rounded bg-white/10 text-emerald-200 font-bold text-[11px]">{s.b}</code>}
                  {s.a}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-wrap gap-2">
          <a
            href={panelUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 h-11 rounded-xl font-black uppercase tracking-wider text-white text-sm"
            style={{
              background: "linear-gradient(180deg, #22c073 0%, #159654 50%, #0d6b3a 100%)",
              boxShadow: "0 10px 30px rgba(13,107,58,0.4)",
            }}
            data-testid="open-promo-panel-btn"
          >
            <LogIn size={15} /> Open Promo Panel
            <ExternalLink size={13} className="opacity-70" />
          </a>
          {pa?.must_change_password && (
            <div className="inline-flex items-center gap-2 px-3 h-11 rounded-xl text-[11px] font-bold uppercase tracking-wider"
              style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.45)", color: "#fbbf24" }}>
              ⚠️ First login required
            </div>
          )}
          {!pa?.email_masked && (
            <div className="inline-flex items-center gap-2 px-3 h-11 rounded-xl text-[11px] font-bold uppercase tracking-wider"
              style={{ background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.45)", color: "#fca5a5" }}>
              ⚠️ No email on file — contact support
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CredBlock({ icon: Icon, color, label, value, hint }) {
  return (
    <div className="rounded-xl p-3 bg-black/35 border border-white/10">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/55 font-semibold mb-1">
        <Icon size={11} style={{ color }} />
        {label}
      </div>
      <div className="text-sm font-black text-white tabular-nums truncate">{value}</div>
      {hint && <div className="text-[10px] text-white/45 mt-0.5">{hint}</div>}
    </div>
  );
}
